import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { Row, Col, Form, Card, Button, Spinner, Table, Badge } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import { FiArrowLeft, FiSave, FiTrash2, FiUserPlus, FiX, FiTruck, FiCalendar, FiUsers, FiDollarSign } from 'react-icons/fi';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import SelectUser from './../components/SelectUser';

export default function Discrecional() {
    const [trip, setTrip] = useState({ name: '', price_discrecional: '', date: moment().format('YYYY-MM-DD') });
    const [travellers, setTravellers] = useState([]);
    const [showSelectUser, setShowSelectUser] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [collectingId, setCollectingId] = useState(null);
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const creating = id === 'create';

    // Load trip
    const load = () => {
        if (!creating) {
            setIsLoading(true);
            tre.api('GET', `/trips/${id}`, {
                context: context,
                success: (r) => {
                    setTrip({
                        ...r,
                        date: r.date ? r.date : moment().format('YYYY-MM-DD'),
                    });
                    setIsLoading(false);
                },
                error: () => setIsLoading(false)
            });
        } else {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        load();
        loadTravellers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creating, id]);

    const loadTravellers = () => {
        if (!creating) {
            tre.api('GET', `/trips/${id}/travellers`, {
                context: context,
                success: (r) => {
                    setTravellers(r);
                },
            });
        }
    };

    const handleForm = (e) => {
        setTrip({ ...trip, [e.target.name]: e.target.value });
    };

    const onChangeDate = (d) => {
        setTrip({ ...trip, date: moment(d).format('YYYY-MM-DD') });
    };

    const save = () => {
        setIsSaving(true);
        if (creating) {
            tre.api('POST', `/companies/${context.idCompany}/trips`, {
                context: context,
                params: { ...trip, type: 'DISCRECIONAL' },
                success: (r) => {
                    NotificationManager.success('Viaje creado');
                    setIsSaving(false);
                    navigate(`/discrecional/${r.id}`);
                    setTrip(r);
                },
                error: () => setIsSaving(false)
            });
        } else {
            tre.api('PUT', `/trips/${trip.id}`, {
                context: context,
                params: trip,
                success: (r) => {
                    setTrip(r);
                    NotificationManager.success('Viaje guardado');
                    setIsSaving(false);
                },
                error: () => setIsSaving(false)
            });
        }
    };

    const remove = () => {
        if (!window.confirm('¿Estás seguro de eliminar este viaje?')) return;
        setIsDeleting(true);
        tre.api('DELETE', `/trips/${trip.id}`, {
            context: context,
            success: () => {
                NotificationManager.success('Viaje eliminado');
                navigate('/discrecionals');
            },
            error: () => setIsDeleting(false)
        });
    };

    const addTraveller = (user) => {
        tre.api('POST', `/trips/${trip.id}/users/${user.id}`, {
            context: context,
            success: () => {
                NotificationManager.success(`Añadido ${user.name} al viaje`);
                loadTravellers();
                setShowSelectUser(false);
            },
        });
    };

    const delTraveller = (traveller) => {
        if (!window.confirm(`¿Eliminar a ${traveller.user?.full_name || 'este pasajero'} del viaje?`)) return;
        tre.api('DELETE', `/travellers/${traveller.id}`, {
            context: context,
            success: () => {
                NotificationManager.success('Pasajero eliminado');
                loadTravellers();
            },
        });
    };

    const collectTraveller = (traveller) => {
        setCollectingId(traveller.id);
        tre.api('PUT', `/payments/${traveller.payment.id}/collect`, {
            context: context,
            success: () => {
                NotificationManager.success('Cobrado');
                loadTravellers();
                setCollectingId(null);
            },
            error: () => setCollectingId(null)
        });
    };

    // Calculate totals
    const totalPassengers = travellers.length;
    const totalCollected = travellers.filter(t => t.payment?.is_paid).reduce((sum, t) => sum + (t.payment?.price || 0), 0);
    const totalPending = travellers.filter(t => t.payment && !t.payment.is_paid).reduce((sum, t) => sum + (t.payment?.price || 0), 0);

    // -----| Render |-----
    if (isLoading) {
        return (
            <div className="container py-4" style={{ maxWidth: '900px' }}>
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#1a73e8' }} />
                    <p className="mt-3 text-muted">Cargando viaje...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ maxWidth: '900px' }}>
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate('/discrecionals')}
                    className="p-0 me-3"
                    style={{ color: '#5f6368' }}
                >
                    <FiArrowLeft size={24} />
                </Button>
                <div className="flex-grow-1">
                    <h4 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiTruck className="me-2" style={{ color: '#1a73e8' }} />
                        {creating ? 'Nuevo Viaje Discrecional' : (trip.name || 'Viaje Discrecional')}
                    </h4>
                    {!creating && trip.date && (
                        <small className="text-muted">
                            <FiCalendar className="me-1" />
                            {moment(trip.date).format('DD MMM YYYY')}
                        </small>
                    )}
                </div>
            </div>

            {/* Trip Info Card */}
            <Card className="mb-4">
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#e8f0fe',
                                color: '#1a73e8'
                            }}
                        >
                            <FiTruck size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Información del Viaje</h6>
                            <small className="text-muted">Datos básicos y precios</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Nombre del viaje</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={trip.name || ''}
                                    onChange={handleForm}
                                    placeholder="Ej: Excursión Sevilla"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Fecha</Form.Label>
                                <DatePicker
                                    className="form-control"
                                    name="date"
                                    dateFormat="dd/MM/yyyy"
                                    selected={moment(trip.date).toDate()}
                                    locale="es"
                                    onChange={onChangeDate}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Precio/Pasajero</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="price_discrecional"
                                        value={trip.price_discrecional || ''}
                                        onChange={handleForm}
                                        placeholder="0.00"
                                    />
                                    <span className="input-group-text">€</span>
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Precio Total</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="price_discrecional_total"
                                        value={trip.price_discrecional_total || ''}
                                        onChange={handleForm}
                                        placeholder="0.00"
                                    />
                                    <span className="input-group-text">€</span>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Travellers Card - Only show if not creating */}
            {!creating && (
                <Card className="mb-4">
                    <Card.Header className="py-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-center">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#e6f4ea',
                                    color: '#34a853'
                                }}
                            >
                                <FiUsers size={18} />
                            </div>
                            <div>
                                <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>
                                    Pasajeros
                                    <Badge bg="secondary" className="ms-2">{totalPassengers}</Badge>
                                </h6>
                                <small className="text-muted">Lista de pasajeros del viaje</small>
                            </div>
                        </div>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setShowSelectUser(true)}
                        >
                            <FiUserPlus className="me-1" />
                            Añadir Pasajero
                        </Button>
                    </Card.Header>
                    <Card.Body className="p-0">
                        {travellers.length > 0 ? (
                            <>
                                <Table hover responsive className="mb-0">
                                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                                        <tr>
                                            <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>PASAJERO</th>
                                            <th className="py-3 text-center" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>ESTADO</th>
                                            <th className="py-3 text-end" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>PRECIO</th>
                                            <th className="py-3 px-3 text-end" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px', width: '180px' }}>ACCIONES</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {travellers.map((traveller) => (
                                            <tr key={traveller.id}>
                                                <td className="py-3 px-3">
                                                    <div className="d-flex align-items-center">
                                                        <div
                                                            className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                            style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                backgroundColor: '#e8f0fe',
                                                                color: '#1a73e8',
                                                                fontSize: '13px',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {(traveller.user?.name || '?').charAt(0).toUpperCase()}
                                                        </div>
                                                        <span style={{ color: '#202124', fontWeight: 500 }}>
                                                            {traveller.user?.full_name || '-'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-3 text-center">
                                                    {traveller.payment ? (
                                                        <Badge
                                                            bg={traveller.payment.is_paid ? 'success' : 'warning'}
                                                            style={{ fontSize: '12px', padding: '6px 12px' }}
                                                        >
                                                            {traveller.payment.is_paid ? 'Cobrado' : 'Pendiente'}
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="secondary" style={{ fontSize: '12px', padding: '6px 12px' }}>
                                                            Sin pago
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className="py-3 text-end">
                                                    <span style={{ fontWeight: 600, color: '#202124' }}>
                                                        {traveller.payment ? `${Number(traveller.payment.price || 0).toFixed(2)} €` : '-'}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-3 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        {traveller.payment && !traveller.payment.is_paid && (
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() => collectTraveller(traveller)}
                                                                disabled={collectingId === traveller.id}
                                                            >
                                                                {collectingId === traveller.id ? (
                                                                    <Spinner size="sm" />
                                                                ) : (
                                                                    <>
                                                                        <FiDollarSign className="me-1" />
                                                                        Cobrar
                                                                    </>
                                                                )}
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => delTraveller(traveller)}
                                                        >
                                                            <FiX />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                {/* Summary Footer */}
                                <div className="d-flex justify-content-end gap-4 p-3" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
                                    <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                        Cobrado: <strong style={{ color: '#34a853' }}>{totalCollected.toFixed(2)} €</strong>
                                    </span>
                                    <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                        Pendiente: <strong style={{ color: '#f9ab00' }}>{totalPending.toFixed(2)} €</strong>
                                    </span>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-5">
                                <FiUsers size={48} className="mb-3" style={{ color: '#dadce0' }} />
                                <p className="text-muted mb-3">No hay pasajeros en este viaje</p>
                                <Button
                                    variant="outline-primary"
                                    onClick={() => setShowSelectUser(true)}
                                >
                                    <FiUserPlus className="me-1" />
                                    Añadir primer pasajero
                                </Button>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Actions */}
            <div className="d-flex justify-content-between">
                <Button
                    variant="light"
                    onClick={() => navigate('/discrecionals')}
                    style={{ padding: '10px 24px' }}
                >
                    Cancelar
                </Button>
                <div className="d-flex gap-2">
                    {!creating && (
                        <Button
                            variant="outline-danger"
                            onClick={remove}
                            disabled={isDeleting}
                            style={{ padding: '10px 24px' }}
                        >
                            {isDeleting ? <Spinner size="sm" className="me-2" /> : <FiTrash2 className="me-2" />}
                            Eliminar
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        onClick={save}
                        disabled={isSaving}
                        style={{ padding: '10px 24px' }}
                    >
                        {isSaving ? <Spinner size="sm" className="me-2" /> : <FiSave className="me-2" />}
                        {creating ? 'Crear Viaje' : 'Guardar Cambios'}
                    </Button>
                </div>
            </div>

            {/* Select User Modal */}
            <SelectUser
                show={showSelectUser}
                toggle={() => setShowSelectUser(!showSelectUser)}
                onSelect={addTraveller}
            />
        </div>
    );
}
